import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';
import type { AiConversationSummary } from '@shared/models/ai-chat.model';
const useStyles = makeStyles()(() => ({
  composerMenuAnchor: {
    position: 'absolute',
    right: 32,
    bottom: 6,
    display: 'inline-flex',
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  composerMenuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    margin: 0,
    padding: 0,
    appearance: 'none',
    border: 'none',
    borderRadius: 5,
    backgroundColor: 'transparent',
    color: 'rgba(0,0,0,0.55)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
      color: 'rgba(0,0,0,0.85)',
    },
  },
}));
type ChatComposerMenuProps = {
  initiallyOpen?: boolean;
  history: AiConversationSummary[] | undefined;
  historyLoading: boolean;
  activeConversationId: string | undefined;
  newChatDisabled: boolean;
  onFetchHistory: () => void;
  onSelectHistory: (id: string) => void;
  onNewChat: () => void;
};

export const ChatComposerMenu: React.FC<ChatComposerMenuProps> = ({
  initiallyOpen = false,
  history,
  historyLoading,
  activeConversationId,
  newChatDisabled,
  onFetchHistory,
  onSelectHistory,
  onNewChat,
}) => {
  const { classes } = useStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    if (initiallyOpen) setAnchor(buttonRef.current);
  }, [initiallyOpen]);
  const items = history ?? [];
  const historyRows = () => {
    if (historyLoading && items.length === 0) {
      return <Menu.Item disabled>Loading…</Menu.Item>;
    }
    if (items.length === 0) {
      return <Menu.Item disabled>No previous conversations</Menu.Item>;
    }
    return items.map((item) => {
      const selected = item.id === activeConversationId;
      return (
        <Menu.Item
          key={item.id}
          selected={selected}
          icon={selected ? <Icon category="mui" icon="Check" /> : undefined}
          onClick={() => void onSelectHistory(item.id)}
        >
          {item.preview.trim()}
        </Menu.Item>
      );
    });
  };

  return (
    <>
      <span className={classes.composerMenuAnchor}>
        <Box
          ref={buttonRef}
          component="button"
          type="button"
          className={classes.composerMenuButton}
          aria-label="Chat menu"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            setAnchor(event.currentTarget);
            onFetchHistory();
          }}
        >
          <Icon category="mui" icon="MoreHoriz" size={13} />
        </Box>
      </span>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Menu.Group label="History">{historyRows()}</Menu.Group>
        <Menu.Divider />
        <Menu.Item
          disabled={newChatDisabled}
          icon={<Icon category="mui" icon="Add" />}
          onClick={() => void onNewChat()}
        >
          New chat
        </Menu.Item>
      </Menu>
    </>
  );
};
